import type {
  KitDefinition,
  KitPresetStructures,
  KitSiteStructure,
} from '../../types';
import { LUMINOSITY_THRESHOLD, THEME_COLOR_1, THEME_COLOR_2 } from './consts';
import {
  computeAverageLuminosity,
  computeLuminosity,
  hexToRgb,
} from './luminosity';
import {
  getComponentsOnImageBG,
  setButtonColor,
  setTextColor,
  setLineColor,
  setSectionColor,
} from './structureUtils';
import { mapStructureById } from '../../utils';

export const adjustColorsForImageBG = async (
  siteStructure: KitSiteStructure,
  kitDefinition: KitDefinition,
  staticMediaUrl: string,
): Promise<KitSiteStructure> => {
  const { darkColor, lightColor } = getColorsFromKit(kitDefinition);
  const [header, sections, footer] = await Promise.all([
    siteStructure.header
      ? adjustElementsColorsForStructure(
          siteStructure.header,
          kitDefinition,
          darkColor,
          lightColor,
          staticMediaUrl,
        )
      : null,

    Promise.all(
      siteStructure.sections.map((section) =>
        adjustElementsColorsForStructure(
          section,
          kitDefinition,
          darkColor,
          lightColor,
          staticMediaUrl,
        ),
      ),
    ),

    siteStructure.footer
      ? adjustElementsColorsForStructure(
          siteStructure.footer,
          kitDefinition,
          darkColor,
          lightColor,
          staticMediaUrl,
        )
      : null,
  ]);
  return { header, sections, footer };
};

function isValidUrlFormat(url: string) {
  try {
    const parsedUrl = new URL(url);
    return !!parsedUrl;
  } catch (e) {
    return false;
  }
}

const adjustElementsColorsForStructure = async (
  section: KitPresetStructures,
  kitDefinition: KitDefinition,
  darkColor: string,
  lightColor: string,
  staticMediaUrl: string,
): Promise<KitPresetStructures> => {
  const elements = getComponentsOnImageBG(section.structure, kitDefinition);
  const mobileElementsById = mapStructureById(section.mobileStructure);
  for (const url of Object.keys(elements)) {
    if (
      elements[url].texts.length === 0 &&
      elements[url].buttons.length === 0 &&
      elements[url].verticalLines.length === 0 &&
      elements[url].horizontalLines.length === 0
    ) {
      continue;
    }

    let [mainColor, secondaryColor] = [lightColor, darkColor];

    if (!elements[url].transparentImage) {
      const avgLuminosity = await computeAverageLuminosity(
        isValidUrlFormat(url) ? url : `${staticMediaUrl}/${url}`,
      );
      [mainColor, secondaryColor] =
        avgLuminosity > LUMINOSITY_THRESHOLD
          ? [darkColor, lightColor]
          : [lightColor, darkColor];
    }

    elements[url].texts.forEach((text) => {
      setTextColor(text, mainColor);
      setSectionColor(section.structure, secondaryColor);
      const mobileText = mobileElementsById[text.id!];
      if (mobileText) {
        setTextColor(mobileText, mainColor);
        setSectionColor(section.mobileStructure, secondaryColor);
      }
    });
    elements[url].buttons.forEach((button) => {
      setButtonColor(button, kitDefinition, mainColor, secondaryColor);
      const mobileButton = mobileElementsById[button.id!];
      if (mobileButton) {
        setButtonColor(mobileButton, kitDefinition, mainColor, secondaryColor);
      }
    });
    elements[url].verticalLines.forEach((verticalLine) => {
      setLineColor(verticalLine, kitDefinition, mainColor);
      const mobileVerticalLine = mobileElementsById[verticalLine.id!];
      if (mobileVerticalLine) {
        setLineColor(mobileVerticalLine, kitDefinition, mainColor);
      }
    });
    elements[url].horizontalLines.forEach((horizontalLine) => {
      setLineColor(horizontalLine, kitDefinition, mainColor);
      const mobileHorizontalLine = mobileElementsById[horizontalLine.id!];
      if (mobileHorizontalLine) {
        setLineColor(mobileHorizontalLine, kitDefinition, mainColor);
      }
    });
  }
  return section;
};

const getColorsFromKit = (
  kitDefinition: KitDefinition,
): {
  darkColor: string;
  lightColor: string;
} => {
  const color1 = kitDefinition.colors[THEME_COLOR_1];
  const color2 = kitDefinition.colors[THEME_COLOR_2];

  if (
    computeLuminosity(hexToRgb(color1)) < computeLuminosity(hexToRgb(color2))
  ) {
    return { darkColor: THEME_COLOR_1, lightColor: THEME_COLOR_2 };
  }
  return { darkColor: THEME_COLOR_2, lightColor: THEME_COLOR_1 };
};
